import { useContext, useEffect } from 'react';
import { AppContext } from '../../Context/AppContext';
import { isEmptyObject } from '../../Utils/Basic';
import { TPreferenceComponentProps } from './PreferenceComponent.type';
import { ECustomTooltipPlacement } from '../../Utils/CustomTooltip/CustomTooltip.type';
import CheckboxGroup from './UtilComponents/CheckboxGroup/CheckboxGroup';
import RadioGroup from './UtilComponents/RadioGroup/RadioGroup';
import CustomTooltip from '../../Utils/CustomTooltip/CustomTooltip';
import DropDownComponent from './UtilComponents/DropDownComponent/DropDownComponent';
import AdvanceSearchComponent from './UtilComponents/AdvanceSearchComponent/AdvanceSearchComponent';
import './PreferenceComponent.scss';

import { fbLogEvent } from '../../Helpers/FirebaseHelper';
import { LEAD, SCREEN } from '../../Constants/AnalyticsConstants';

export const renderWithSwitch = (props: any) => {
  const {
    slug,
    type,
    defaultValue,
    userSelectedValue,
    items,
    handleChange,
    isAdvanceSearch,
    validationError,
  } = props;
  switch (type) {
    case 'radio':
      return (
        <RadioGroup
          items={items}
          userSelectedValue={userSelectedValue}
          defaultValue={defaultValue}
          handleChange={handleChange}
          type={type}
          slug={slug}
          validationError={validationError}
        />
      );
    case 'checkbox':
      return (
        <CheckboxGroup
          items={items}
          userSelectedValue={userSelectedValue}
          defaultValue={defaultValue}
          handleChange={handleChange}
          type={type}
          slug={slug}
          isAdvanceSearch={isAdvanceSearch}
          validationError={validationError}
        />
      );
    case 'dropdown':
      return (
        <DropDownComponent
          items={items}
          userSelectedValue={userSelectedValue}
          defaultValue={defaultValue}
          handleChange={handleChange}
          type={type}
          slug={slug}
          validationError={validationError}
        />
      );
    default:
      return null;
  }
};

const PreferenceComponent = (props: TPreferenceComponentProps) => {
  const {
    slug,
    label,
    content,
    isExpanded,
    handleAccordClick,
    handleChange,
    storedPreferences,
    userAlteredData,
    maxMilesError,
  } = props;
  const { isMobile, preferencesClicked, setPreferencesClicked } = useContext(AppContext);

  useEffect(() => {
    return () => {
      setPreferencesClicked(false);
    };
  }, []);

  const transformLabel = (label: string): string => {
    return label.toLowerCase().replace(/\s+/g, '_');
  };

  return (
    <div className={`Preference_Component ${slug}`} key={slug}>
      <div className="Preference_Header">
        <div className="Preference_Title">
          {label}
          {!isEmptyObject(userAlteredData) && (
            <CustomTooltip
              message={'Unsaved changes'}
              isOnClick={false}
              placement={ECustomTooltipPlacement?.right}
              slug={slug}
            >
              <img src="/oo/assets/icon/alert-preference.svg" alt="Alert-Icon" />
            </CustomTooltip>
          )}
        </div>
        <div
          className="Expand_Icon_Container"
          onClick={() => {
            handleAccordClick(slug);
            if (!isExpanded) {
              fbLogEvent(`ue_${transformLabel(label)}`, {
                screen: SCREEN.PREFERENCES,
                lead: isMobile
                  ? SCREEN.MANAGE_MENU
                  : preferencesClicked
                    ? SCREEN.TOP_MENU_WEB
                    : LEAD.LEFT_NAV,
              });
            }
          }}
        >
          {!isMobile ? (
            <img
              src={`/oo/assets/icon/down arrow@2x.png`}
              className={`${isExpanded ? 'up' : ''} ml-3`}
              alt="Down-Arrow-Icon"
            />
          ) : slug === 'advanceSearch' && isExpanded ? null : (
            <img
              src={`/oo/assets/icon/${isExpanded ? 'minus-icon' : 'plus-icon'}.svg`}
              alt="Expand-Icon"
            />
          )}
        </div>
      </div>
      {isExpanded && !!content && (
        <div className="Content_Container">
          {slug === 'advancedSearch' ? (
            <AdvanceSearchComponent
              content={content}
              userAlteredData={userAlteredData}
              handleChange={handleChange}
              storedPreferences={storedPreferences}
              maxMilesError={maxMilesError}
            />
          ) : (
            content?.map((contentItem: any) => {
              return (
                <div key={contentItem?.API_KEY} className="Content_Item">
                  <div className="Content_Header">
                    {contentItem?.API_KEY != 'preferredloadsortby' && (
                      <p className="Content_Item_Title">{contentItem?.CONTENT_TITLE}</p>
                    )}
                  </div>

                  {contentItem?.API_KEY === 'preferredloadsortby' ? (
                    <div className="SortingContainer">
                      <div className="SortingContainer_label">Sort By </div>
                      <div
                        className="Sort_direction"
                        data-testid="Sort_direction"
                        onClick={() => {
                          let sortDirection = userAlteredData?.[contentItem?.API_KEY] ? userAlteredData?.[contentItem?.API_KEY] : storedPreferences?.[contentItem?.API_KEY] || contentItem?.DEFAULT_VALUE;
                          sortDirection = sortDirection === 'asc' ? 'dsc' : 'asc';
                          handleChange('radio', contentItem?.API_KEY, sortDirection);
                        }}
                      >
                        <img
                          src={`/oo/assets/icon/sort_direction_${userAlteredData?.[contentItem?.API_KEY] ? userAlteredData?.[contentItem?.API_KEY] : storedPreferences?.[contentItem?.API_KEY] || contentItem?.DEFAULT_VALUE}.svg`}
                          data-testid="sortDirection_icon"
                        />
                      </div>
                    </div>
                  ) : (
                    renderWithSwitch({
                      slug: contentItem?.API_KEY,
                      type: contentItem?.OPTION_TYPE,
                      defaultValue: contentItem?.CONTENT_VALUE?.some(
                        (_item: any) =>
                          _item?.OPTION_VALUE === storedPreferences?.[contentItem?.API_KEY],
                      )
                        ? storedPreferences?.[contentItem?.API_KEY]
                        : contentItem?.DEFAULT_VALUE,
                      userSelectedValue: userAlteredData[contentItem?.API_KEY],
                      items: contentItem?.CONTENT_VALUE,
                      handleChange: handleChange,
                    })
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default PreferenceComponent;
